import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { leadgenApi, type LeadGenForm } from '@/api/leadgen.api';
import { getApiBaseUrl } from '@/config/env';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';

const SAMPLE_PAYLOAD = `{
  "formPublicKey": "<uuid-from-published-form>",
  "fullName": "Jane Doe",
  "email": "jane@example.com",
  "phone": "+15551234567",
  "leadSource": "facebook_lead_ad",
  "utmCampaign": "spring-promo",
  "consentGiven": true,
  "fields": { "company": "Acme Corp" }
}`;

export function LeadGenWebhooksPage() {
  const [forms, setForms] = useState<LeadGenForm[]>([]);
  const publishedForms = forms.filter((f) => f.published);
  const { rows: paginatedPublishedForms, tablePaginationProps: publishedFormsPaginationProps } =
    useClientPaginatedRows(publishedForms);
  const apiBase = getApiBaseUrl();

  useEffect(() => {
    leadgenApi.listForms().then(setForms).catch((e) => showApiError(e, 'Failed to load forms'));
  }, []);

  return (
    <div className="page-content">
      <PageHeader
        title="Lead ad webhooks"
        description="Facebook and Google Lead Ads POST into LeadIntakeService — same path as form submissions"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Webhooks' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4 space-y-3">
          <h3 className="font-semibold">Endpoints</h3>
          <p className="text-sm text-slate-600">
            Configure your ad platform to POST JSON to these URLs. Include <code>formPublicKey</code> from a
            published form and <code>consentGiven: true</code>.
          </p>
          <div className="rounded bg-slate-50 dark:bg-slate-900 p-3 text-xs font-mono break-all">
            POST {apiBase}/v1/lead-gen/webhooks/facebook
          </div>
          <div className="rounded bg-slate-50 dark:bg-slate-900 p-3 text-xs font-mono break-all">
            POST {apiBase}/v1/lead-gen/webhooks/google
          </div>
        </div>

        <div className="card p-4">
          <h3 className="font-semibold mb-2">Sample payload</h3>
          <pre className="text-xs bg-slate-50 dark:bg-slate-900 p-3 rounded overflow-x-auto">{SAMPLE_PAYLOAD}</pre>
        </div>
      </div>

      <div className="card p-4">
        <h3 className="font-semibold mb-3">Published forms (formPublicKey)</h3>
        {publishedForms.length === 0 ? (
          <p className="text-sm text-slate-500">
            No published forms —{' '}
            <Link to="/lead-gen/forms" className="text-primary-600 hover:underline">
              publish a form
            </Link>{' '}
            first.
          </p>
        ) : (
          <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="p-2">Name</th>
                <th className="p-2">Public key</th>
                <th className="p-2">Embed</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPublishedForms.map((f) => (
                <tr key={f.id} className="border-b">
                  <td className="p-2">{f.name}</td>
                  <td className="p-2 font-mono text-xs">{f.publicKey}</td>
                  <td className="p-2">
                    <Link to={`/embed/lead/${f.publicKey}`} className="text-primary-600 text-xs hover:underline">
                      Test embed
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...publishedFormsPaginationProps} hasRows={publishedForms.length > 0} />
          </>
        )}
      </div>
    </div>
  );
}
